
import { useToast } from "@/components/ui/use-toast";
import { LeadAccount } from "@/types/strategy-types";
import { 
  createLeadAccountApi, 
  updateLeadAccountApi, 
  deleteLeadAccountApi, 
  getUserProviderRole 
} from "@/api/leadAccounts";
import { supabase } from "@/integrations/supabase/client";

export const useLeadAccountOperations = (reloadAccounts: () => Promise<void>) => {
  const { toast } = useToast();

  const createLeadAccount = async (leadAccount: Omit<LeadAccount, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      // Get the provider ID if user is a strategy provider
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) throw new Error("Not authenticated");
      
      const roleData = await getUserProviderRole(session.session.user.id);

      // If user is a strategy provider, set the provider_id
      if (roleData?.role === 'strategy_provider' && roleData?.provider_id) {
        leadAccount.strategy_provider_id = roleData.provider_id;
      }

      await createLeadAccountApi(leadAccount);

      toast({
        title: "Success",
        description: "Lead account created successfully",
      });

      await reloadAccounts();
    } catch (error) {
      console.error('Error creating lead account:', error);
      toast({
        title: "Error",
        description: "Failed to create lead account",
        variant: "destructive",
      });
    }
  };

  const updateLeadAccount = async (id: string, leadAccount: Partial<LeadAccount>) => {
    try {
      await updateLeadAccountApi(id, leadAccount);

      toast({
        title: "Success",
        description: "Lead account updated successfully",
      });

      await reloadAccounts();
    } catch (error) {
      console.error('Error updating lead account:', error);
      toast({
        title: "Error",
        description: "Failed to update lead account",
        variant: "destructive",
      });
    }
  };

  const deleteLeadAccount = async (id: string) => {
    try {
      await deleteLeadAccountApi(id);

      toast({
        title: "Success",
        description: "Lead account deleted successfully",
      });

      await reloadAccounts();
    } catch (error) {
      console.error('Error deleting lead account:', error);
      toast({
        title: "Error",
        description: "Failed to delete lead account",
        variant: "destructive",
      });
    }
  };

  return {
    createLeadAccount,
    updateLeadAccount,
    deleteLeadAccount
  };
};
